<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
// Layout 1:1 com /plans real Lais (DOM + dynamic.js):
//  - Title "Planos disponíveis"
//  - Subtitle "Escolha o plano que mais se adequa ao seu negócio."
//  - Tabs "Planos mensais" | "Planos anuais"
//  - Preços reais extraídos do dynamic.js/plans.json, com 6 tiers por ciclo.
//  - Botão Intercom: "Gostaria de contratar o plano <X> <ciclo>"
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import PlanosAPI from 'dashboard/api/planos';
import FinanceiroAPI from 'dashboard/api/financeiro';
import { useAlert } from 'dashboard/composables';
import { useTracking } from 'dashboard/composables/useTracking';
import {
  buildPlanContactMessage,
  defaultAnnualPlans,
  defaultMonthlyPlans,
  normalizeCatalogPlan,
  usageFromBillingPayload,
} from '../utils/plansCatalog';

const route = useRoute();
const router = useRouter();
const { track } = useTracking();
const routeString = value => (Array.isArray(value) ? value[0] : value);
const normalizeCycle = value => (value === 'anual' ? 'anual' : 'mensal');

const isLoading = ref(false);
const checkoutUrl = ref('');
const subscriptionUrl = ref('');
const billingSource = ref('manual');
const planoAtual = ref({});
const billingCycle = ref(normalizeCycle(routeString(route.query.ciclo)));
const statusFilter = ref(routeString(route.query.status) || 'todos');
const detailsPlanId = ref(routeString(route.query.detalhes) || '');
const cancelPlan = ref(null);
const cancelReason = ref('');
const subscriptionUpdating = ref(false);
// PL5 — aviso de consumo (100%) do plano, alimentado por cycle_usage.
const usoCiclo = ref({ percentual: 0, atendimentos: 0, limite: 0 });
const consumoPercentual = computed(() =>
  Math.round(Number(usoCiclo.value?.percentual || 0))
);
const atingiuLimite = computed(() => consumoPercentual.value >= 100);

const basePlanId = value => String(value || '').replace(/-anual$/, '');
const activePlanBaseId = computed(() => basePlanId(planoAtual.value?.id));
const hasActivePlan = computed(() => Boolean(activePlanBaseId.value));
const activePlanEndsAt = computed(
  () => planoAtual.value?.cancela_em || planoAtual.value?.fim_ciclo
);
const isActivePlan = plano =>
  hasActivePlan.value && basePlanId(plano.id) === activePlanBaseId.value;

const contratar = async plano => {
  track('plans_upgrade_clicked', {
    plano_id: basePlanId(plano.id),
    ciclo: billingCycle.value,
  });
  const msg = buildPlanContactMessage(plano, billingCycle.value);
  try {
    if (checkoutUrl.value) {
      const { data } = await PlanosAPI.checkout(checkoutUrl.value);
      if (data?.redirect_url) {
        window.location = data.redirect_url;
        return;
      }
    }

    if (!checkoutUrl.value && subscriptionUrl.value) {
      await PlanosAPI.setup(subscriptionUrl.value);
      useAlert(
        'Estamos preparando o portal de cobrança da sua conta. Atualize a página em alguns instantes.'
      );
      return;
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível abrir o portal de contratação.'
    );
    return;
  }

  if (typeof window !== 'undefined' && typeof window.Intercom === 'function') {
    window.Intercom('showNewMessage', msg);
    return;
  }
  window.location.href = `mailto:suporte@imudei.app?subject=${encodeURIComponent(msg)}`;
};

const planosMensais = defaultMonthlyPlans();

const planosAnuais = defaultAnnualPlans();

const ciclos = [
  { value: 'mensal', label: 'Planos mensais' },
  { value: 'anual', label: 'Planos anuais' },
];

const planos = computed(() =>
  billingCycle.value === 'anual' ? planosAnuais : planosMensais
);

const filteredPlanos = computed(() => {
  if (statusFilter.value === 'ativo') {
    return planos.value.filter(plano => isActivePlan(plano));
  }
  if (statusFilter.value === 'disponiveis') {
    return planos.value.filter(plano => !isActivePlan(plano));
  }
  return planos.value;
});

const allPlansForCycle = computed(() => planos.value);

const detailPlan = computed(
  () =>
    allPlansForCycle.value.find(
      plan => basePlanId(plan.id) === basePlanId(detailsPlanId.value)
    ) || null
);

const lowerTierPlan = computed(() => {
  const activeIndex = planosMensais.findIndex(
    plan => basePlanId(plan.id) === activePlanBaseId.value
  );
  if (activeIndex <= 0) return null;
  return planosMensais[activeIndex - 1];
});

const formatDateOnly = value => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString('pt-BR');
};

const selectCycle = cycle => {
  billingCycle.value = normalizeCycle(cycle);
  const query = { ...route.query };
  if (billingCycle.value === 'mensal') {
    delete query.ciclo;
  } else {
    query.ciclo = billingCycle.value;
  }
  router.replace({ query }).catch(() => {});
};

const selectStatusFilter = value => {
  statusFilter.value = value;
  const query = { ...route.query };
  if (value === 'todos') delete query.status;
  else query.status = value;
  router.replace({ query }).catch(() => {});
};

const syncDetailsQuery = planId => {
  const query = { ...route.query };
  if (planId) query.detalhes = planId;
  else delete query.detalhes;
  router.replace({ query }).catch(() => {});
};

const formatBRL = v =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: Number(v) % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(Number(v));

const hydratePlans = async () => {
  isLoading.value = true;
  try {
    const { data } = await PlanosAPI.index();
    checkoutUrl.value = data.portal_url || '';
    subscriptionUrl.value = data.subscription_url || '';
    billingSource.value = data.billing_source || 'manual';
    planoAtual.value = data.plano_atual || {};
    if (planoAtual.value?.id) {
      track('plans_active_plan_viewed', {
        plano_id: basePlanId(planoAtual.value.id),
        ciclo: billingCycle.value,
        billing_source: billingSource.value,
      });
    }

    if (data.catalogo?.mensal?.length) {
      planosMensais.splice(
        0,
        planosMensais.length,
        ...data.catalogo.mensal.map(plan =>
          normalizeCatalogPlan(plan, 'mensal')
        )
      );
    }

    if (data.catalogo?.anual?.length) {
      planosAnuais.splice(
        0,
        planosAnuais.length,
        ...data.catalogo.anual.map(plan => normalizeCatalogPlan(plan, 'anual'))
      );
    }
  } catch (_error) {
    // mantém fallback estático se o catálogo ainda não estiver configurado
  } finally {
    isLoading.value = false;
  }
};

const planRank = plano =>
  planosMensais.findIndex(plan => basePlanId(plan.id) === basePlanId(plano.id));

const planStatusLabel = plano => {
  if (!isActivePlan(plano)) return 'Disponível';
  if (planoAtual.value.cancelamento_agendado) {
    return `Cancelamento agendado para ${formatDateOnly(activePlanEndsAt.value)}`;
  }
  return 'Plano ativo';
};

const planCtaLabel = plano => {
  if (isActivePlan(plano)) return 'Ver detalhes';
  if (!hasActivePlan.value)
    return plano.cta === 'vendas' ? 'Atendimento comercial' : 'Escolher plano';
  if (planRank(plano) > planRank(planoAtual.value)) return 'Fazer upgrade';
  return 'Fazer downgrade';
};

const openDetails = (plano, origem = 'card') => {
  detailsPlanId.value = plano?.id || '';
  syncDetailsQuery(detailsPlanId.value);
  track('plans_details_opened', {
    plano_id: basePlanId(plano?.id),
    origem,
  });
};

const closeDetails = () => {
  detailsPlanId.value = '';
  syncDetailsQuery('');
};

const openCancel = plano => {
  cancelPlan.value = plano;
  cancelReason.value = '';
  track('plans_cancel_intent', {
    plano_id: basePlanId(plano?.id),
  });
};

const closeCancel = () => {
  cancelPlan.value = null;
  cancelReason.value = '';
};

const submitSubscriptionAction = async payload => {
  subscriptionUpdating.value = true;
  try {
    const { data } = await PlanosAPI.cancelSubscription(payload);
    useAlert(data.mensagem || 'Assinatura atualizada com sucesso.');
    if (payload.acao === 'cancelar') {
      track('plans_cancel_confirmed', {
        plano_id: activePlanBaseId.value,
        motivo: Boolean(payload.motivo),
        cancela_em: data.plano_atual?.cancela_em,
      });
    } else if (payload.acao === 'downgrade') {
      track('plans_downgrade_confirmed', {
        de_plano_id: activePlanBaseId.value,
        para_plano_id: payload.downgrade_para,
        efetivo_em: data.plano_atual?.cancela_em,
      });
    } else if (payload.acao === 'reativar') {
      track('plans_reactivated', {
        plano_id: activePlanBaseId.value,
      });
    }
    closeCancel();
    await hydratePlans();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível atualizar a assinatura. Tente novamente.'
    );
  } finally {
    subscriptionUpdating.value = false;
  }
};

const confirmCancel = () =>
  submitSubscriptionAction({
    acao: 'cancelar',
    motivo: cancelReason.value || undefined,
  });

const confirmDowngrade = plano =>
  submitSubscriptionAction({
    acao: 'downgrade',
    downgrade_para: basePlanId(plano.id),
    motivo: cancelReason.value || undefined,
  });

const reactivatePlan = () => submitSubscriptionAction({ acao: 'reativar' });

const handlePrimaryAction = plano => {
  if (isActivePlan(plano)) {
    openDetails(plano);
    return;
  }
  contratar(plano);
};

const scrollToPlans = () => {
  track('plans_upgrade_clicked', {
    plano_id: activePlanBaseId.value || 'consumo_100',
    ciclo: billingCycle.value,
    origem: 'banner_consumo',
  });
  if (typeof document === 'undefined') return;
  document.getElementById('planos-disponiveis')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

const canSelfManageSubscription = computed(
  () =>
    Boolean(planoAtual.value?.cancelavel) && billingSource.value !== 'manual'
);

const loadConsumo = async () => {
  try {
    const { data } = await FinanceiroAPI.cobranca({
      ciclo: billingCycle.value,
    });
    usoCiclo.value = usageFromBillingPayload(data) || usoCiclo.value;
  } catch {
    try {
      const { data } = await FinanceiroAPI.resumo();
      usoCiclo.value = usageFromBillingPayload(data) || usoCiclo.value;
    } catch {
      // consumo é informativo; falha não bloqueia a tela de planos.
    }
  }
};

onMounted(() => {
  hydratePlans();
  loadConsumo();
});
watch(
  () => route.query.ciclo,
  value => {
    const nextCycle = normalizeCycle(routeString(value));
    if (nextCycle !== billingCycle.value) {
      billingCycle.value = nextCycle;
    }
  }
);
watch(
  () => route.query.detalhes,
  value => {
    detailsPlanId.value = routeString(value) || '';
  }
);
watch(billingCycle, () => loadConsumo());
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex flex-col h-full bg-white font-redHatText overflow-hidden"
  >
    <LaisHeader
      title="Planos disponíveis"
      helper="Escolha o plano que mais se adequa ao seu negócio."
    />

    <div class="flex-1 overflow-auto p-4 sm:p-6 lg:p-10">
      <!-- PL5 — aviso de consumo 100% do plano -->
      <div
        v-if="atingiuLimite"
        class="mb-5 flex flex-col gap-2 rounded-lais border border-[#f0c36d] bg-[#fff8e6] px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <p class="text-[14px] font-semibold text-[#92670c]">
            Você atingiu 100% do consumo do plano
          </p>
          <p class="text-[12px] text-[#92670c]">
            Atualize seu plano e economize com mais leads atendidos.
          </p>
        </div>
        <button
          type="button"
          class="rounded-lais bg-[#92670c] px-4 py-2 text-center text-[13px] font-semibold text-white"
          @click="scrollToPlans"
        >
          Atenda mais
        </button>
      </div>

      <div v-if="isLoading" class="w-full pb-4 text-[13px] text-[#a0aec0]">
        Carregando planos...
      </div>
      <div
        class="mb-5 inline-flex rounded-lais border border-[#e5e7eb] bg-white p-1"
        aria-label="Selecionar ciclo de cobrança"
      >
        <button
          v-for="cycle in ciclos"
          :key="cycle.value"
          type="button"
          class="min-h-10 rounded-[6px] px-4 text-[13px] font-semibold transition-colors"
          :class="
            billingCycle === cycle.value
              ? 'bg-[#49317a] text-white'
              : 'text-[#4a5568] hover:bg-[#f8f4fc] hover:text-[#49317a]'
          "
          @click="selectCycle(cycle.value)"
        >
          {{ cycle.label }}
        </button>
      </div>
      <div
        class="mb-5 ml-0 inline-flex rounded-lais border border-[#e5e7eb] bg-white p-1 md:ml-3"
        aria-label="Filtrar status do plano"
      >
        <button
          v-for="item in [
            { value: 'todos', label: 'Todos' },
            { value: 'ativo', label: 'Plano ativo' },
            { value: 'disponiveis', label: 'Disponíveis' },
          ]"
          :key="item.value"
          type="button"
          class="min-h-10 rounded-[6px] px-4 text-[13px] font-semibold transition-colors"
          :class="
            statusFilter === item.value
              ? 'bg-[#49317a] text-white'
              : 'text-[#4a5568] hover:bg-[#f8f4fc] hover:text-[#49317a]'
          "
          @click="selectStatusFilter(item.value)"
        >
          {{ item.label }}
        </button>
      </div>
      <div
        v-if="!hasActivePlan"
        class="mb-5 rounded-lais border border-[#e5e7eb] bg-[#fafafa] px-4 py-3 text-[13px] font-semibold text-[#4a5568]"
      >
        Você ainda não possui um plano ativo.
      </div>
      <div
        class="mb-5 flex flex-col gap-3 rounded-lais border border-[#e5e7eb] bg-[#fafafa] px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <p class="text-[14px] font-semibold text-[#1a1a1a]">
            Cobrança de atendimento
          </p>
          <p class="text-[12px] text-[#4a5568]">
            Atendimentos contratados para 12 meses. Atendimento comercial: De
            segunda à sexta das 09h às 18h.
          </p>
        </div>
        <RouterLink
          :to="{ name: 'financeiro_index' }"
          class="inline-flex h-10 items-center justify-center rounded-lais border border-[#e5e7eb] bg-white px-4 text-[13px] font-semibold text-[#49317a] hover:bg-[#f8f4fc]"
        >
          Ver cobrança
        </RouterLink>
      </div>
      <div id="planos-disponiveis" class="grid w-full gap-5 md:grid-cols-3">
        <article
          v-for="plano in filteredPlanos"
          :key="plano.id"
          class="relative flex flex-col gap-4 p-6 bg-white border rounded-lais transition-all"
          :class="
            isActivePlan(plano)
              ? 'border-[#28E4A8] shadow-lais-pop ring-2 ring-[#28E4A8]/20'
              : plano.destaque
                ? 'border-[#49317a] shadow-lais-pop ring-2 ring-[#49317a]/10'
                : 'border-[#e5e7eb] shadow-lais-card'
          "
        >
          <span
            v-if="isActivePlan(plano)"
            class="absolute -top-3 right-6 text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-[#28E4A8] text-[#1a1a1a]"
          >
            Plano ativo
          </span>
          <h2
            class="text-[24px] font-semibold leading-none font-redHatDisplay mt-2"
            :class="plano.destaque ? 'text-[#49317a]' : 'text-[#1a1a1a]'"
          >
            {{ plano.nome }}
          </h2>

          <div v-if="plano.preco != null" class="flex items-baseline gap-1">
            <span
              class="text-[34px] font-semibold leading-none text-[#1a1a1a] font-redHatDisplay"
            >
              {{ formatBRL(plano.preco) }}
            </span>
            <span class="text-[13px] text-[#a0aec0]">/mês</span>
          </div>
          <p
            v-else
            class="text-[24px] font-semibold leading-tight text-[#1a1a1a] font-redHatDisplay"
          >
            {{ plano.precoLabel || 'Sob consulta' }}
          </p>

          <p class="text-[13px] text-[#4a5568]">
            <strong class="text-[#1a1a1a]">
              {{
                plano.atendimentosLabel || `${plano.atendimentos} atendimentos`
              }}
            </strong>
          </p>
          <p
            v-if="plano.annualNote"
            class="text-[12px] font-semibold text-[#49317a]"
          >
            {{ plano.annualNote }}
          </p>
          <div
            v-if="
              plano.precoPorAtendimento != null ||
              plano.precoPorAtendimentoLabel
            "
            class="rounded-lais border border-[#ece8f5] bg-[#fbf9ff] px-3 py-2 text-[12px] text-[#4a5568]"
          >
            <span class="block font-semibold text-[#49317a]">
              Preço por atendimento
            </span>
            <strong class="text-[#1a1a1a]">
              {{
                plano.precoPorAtendimento != null
                  ? formatBRL(plano.precoPorAtendimento)
                  : plano.precoPorAtendimentoLabel
              }}
            </strong>
          </div>
          <p
            v-if="isActivePlan(plano) && planoAtual.cancelamento_agendado"
            class="w-fit rounded-full bg-[#fff8e6] px-3 py-1 text-[12px] font-semibold text-[#92670c]"
          >
            {{ planStatusLabel(plano) }}
          </p>

          <ul
            class="flex flex-col gap-2.5 flex-1 border-t border-[#f3f4f6] pt-4 mt-2"
          >
            <li
              v-for="(f, i) in plano.features"
              :key="i"
              class="flex items-start gap-2 text-[13px] text-[#4a5568] leading-snug"
            >
              <span
                class="i-lucide-check size-4 text-[#28E4A8] shrink-0 mt-0.5"
                aria-hidden="true"
              />
              {{ f }}
            </li>
          </ul>

          <button
            type="button"
            class="w-full h-11 rounded-lais font-semibold text-[14px] transition-colors"
            :class="
              plano.destaque
                ? 'bg-[#49317a] text-white hover:bg-[#3d2966]'
                : 'bg-[#f5f5f5] text-[#4a5568] hover:bg-[#ece8f5] hover:text-[#49317a]'
            "
            @click="handlePrimaryAction(plano)"
          >
            {{ planCtaLabel(plano) }}
          </button>
          <button
            type="button"
            class="h-10 rounded-lais border border-[#e5e7eb] text-[13px] font-semibold text-[#49317a] transition-colors hover:bg-[#f8f4fc]"
            @click="openDetails(plano)"
          >
            Ver detalhes
          </button>
          <button
            v-if="isActivePlan(plano) && !planoAtual.cancelamento_agendado"
            type="button"
            class="h-10 rounded-lais border border-[#ffd1d1] text-[13px] font-semibold text-[#b42318] transition-colors hover:bg-[#fff5f5] disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="!canSelfManageSubscription"
            :title="
              canSelfManageSubscription
                ? 'Cancelar assinatura'
                : 'Cancelamento gerenciado pelo suporte'
            "
            @click="openCancel(plano)"
          >
            Cancelar
          </button>
          <button
            v-if="isActivePlan(plano) && planoAtual.cancelamento_agendado"
            type="button"
            class="h-10 rounded-lais border border-[#28E4A8] text-[13px] font-semibold text-[#027a48] transition-colors hover:bg-[#ecfdf3]"
            :disabled="subscriptionUpdating"
            @click="reactivatePlan"
          >
            Reativar plano
          </button>
        </article>
      </div>
    </div>

    <LaisModal
      :show="Boolean(detailPlan)"
      :title="
        detailPlan
          ? `Detalhes do plano ${detailPlan.nome}`
          : 'Detalhes do plano'
      "
      max-width-class="max-w-[900px]"
      @close="closeDetails"
    >
      <div class="overflow-x-auto">
        <table class="min-w-[720px] w-full text-left text-sm">
          <thead>
            <tr class="border-b border-[#e5e7eb]">
              <th class="py-3 pr-4 font-semibold text-[#4a5568]">Campo</th>
              <th
                v-for="plano in allPlansForCycle"
                :key="plano.id"
                class="py-3 pr-4 font-semibold text-[#1a1a1a]"
                :class="isActivePlan(plano) ? 'text-[#49317a]' : ''"
              >
                {{ plano.nome }}
                <span
                  v-if="isActivePlan(plano)"
                  class="ml-2 rounded-full bg-[#28E4A8] px-2 py-0.5 text-[10px] text-[#1a1a1a]"
                >
                  Plano ativo
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-b border-[#f3f4f6]">
              <td class="py-3 pr-4 font-semibold text-[#4a5568]">Preço</td>
              <td
                v-for="plano in allPlansForCycle"
                :key="`preco-${plano.id}`"
                class="py-3 pr-4"
              >
                {{
                  plano.preco != null
                    ? formatBRL(plano.preco)
                    : plano.precoLabel || 'Sob consulta'
                }}
              </td>
            </tr>
            <tr class="border-b border-[#f3f4f6]">
              <td class="py-3 pr-4 font-semibold text-[#4a5568]">Limite</td>
              <td
                v-for="plano in allPlansForCycle"
                :key="`limite-${plano.id}`"
                class="py-3 pr-4"
              >
                {{
                  plano.atendimentosLabel ||
                  `${plano.atendimentos} atendimentos`
                }}
              </td>
            </tr>
            <tr
              v-if="
                allPlansForCycle.some(
                  plano =>
                    plano.precoPorAtendimento != null ||
                    plano.precoPorAtendimentoLabel
                )
              "
              class="border-b border-[#f3f4f6]"
            >
              <td class="py-3 pr-4 font-semibold text-[#4a5568]">
                Preço por atendimento
              </td>
              <td
                v-for="plano in allPlansForCycle"
                :key="`preco-atendimento-${plano.id}`"
                class="py-3 pr-4"
              >
                {{
                  plano.precoPorAtendimento != null
                    ? formatBRL(plano.precoPorAtendimento)
                    : plano.precoPorAtendimentoLabel || '-'
                }}
              </td>
            </tr>
            <tr class="border-b border-[#f3f4f6]">
              <td class="py-3 pr-4 font-semibold text-[#4a5568]">Status</td>
              <td
                v-for="plano in allPlansForCycle"
                :key="`status-${plano.id}`"
                class="py-3 pr-4"
              >
                {{ planStatusLabel(plano) }}
              </td>
            </tr>
            <tr>
              <td class="py-3 pr-4 font-semibold text-[#4a5568]">
                Funcionalidades
              </td>
              <td
                v-for="plano in allPlansForCycle"
                :key="`features-${plano.id}`"
                class="py-3 pr-4 align-top"
              >
                <ul class="space-y-1">
                  <li v-for="feature in plano.features" :key="feature">
                    {{ feature }}
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <template #footer>
        <div class="flex flex-wrap justify-end gap-3">
          <RouterLink
            :to="{ name: 'financeiro_index' }"
            class="inline-flex h-10 items-center rounded-lais border border-[#e5e7eb] px-4 text-sm font-semibold text-[#49317a] hover:bg-[#f8f4fc]"
          >
            Ver histórico de cobrança
          </RouterLink>
          <button
            type="button"
            class="h-10 rounded-lais bg-[#49317a] px-4 text-sm font-semibold text-white hover:bg-[#3d2966]"
            @click="closeDetails"
          >
            Fechar
          </button>
        </div>
      </template>
    </LaisModal>

    <LaisModal
      :show="Boolean(cancelPlan)"
      :title="
        cancelPlan ? `Cancelar plano ${cancelPlan.nome}?` : 'Cancelar plano'
      "
      max-width-class="max-w-[560px]"
      @close="closeCancel"
    >
      <div v-if="cancelPlan" class="space-y-4 text-sm text-[#4a5568]">
        <p>
          Seu plano permanece ativo até
          <strong class="text-[#1a1a1a]">
            {{ formatDateOnly(activePlanEndsAt) }}
          </strong>
          . Até essa data você mantém acesso completo.
        </p>
        <div class="rounded-lais border border-[#e5e7eb] bg-[#fafafa] p-4">
          <p class="font-semibold text-[#1a1a1a]">
            Funcionalidades que serão perdidas:
          </p>
          <ul class="mt-2 list-disc space-y-1 pl-5">
            <li v-for="feature in cancelPlan.features" :key="feature">
              {{ feature }}
            </li>
          </ul>
        </div>
        <label class="flex flex-col gap-2">
          Motivo
          <select
            v-model="cancelReason"
            class="h-10 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[#1a1a1a] focus:border-[#49317a] focus:outline-none"
          >
            <option value="">Prefiro não informar</option>
            <option value="preco">Preço</option>
            <option value="uso_baixo">Uso baixo</option>
            <option value="troca_de_ferramenta">Troca de ferramenta</option>
          </select>
        </label>
        <button
          v-if="lowerTierPlan"
          type="button"
          class="inline-flex h-10 items-center rounded-lais border border-[#e5e7eb] px-4 text-sm font-semibold text-[#49317a] hover:bg-[#f8f4fc]"
          :disabled="subscriptionUpdating"
          @click="confirmDowngrade(lowerTierPlan)"
        >
          Fazer downgrade para {{ lowerTierPlan.nome }}
        </button>
      </div>
      <template #footer>
        <div class="flex flex-wrap justify-end gap-3">
          <RouterLink
            :to="{ name: 'financeiro_index' }"
            class="inline-flex h-10 items-center rounded-lais border border-[#e5e7eb] px-4 text-sm font-semibold text-[#49317a] hover:bg-[#f8f4fc]"
          >
            Ver histórico de cobrança
          </RouterLink>
          <button
            type="button"
            class="h-10 rounded-lais px-4 text-sm font-semibold text-[#4a5568] hover:text-[#1a1a1a]"
            @click="closeCancel"
          >
            Voltar
          </button>
          <button
            type="button"
            class="inline-flex h-10 items-center gap-2 rounded-lais bg-[#b42318] px-4 text-sm font-semibold text-white hover:bg-[#912018] disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="subscriptionUpdating"
            @click="confirmCancel"
          >
            <span
              v-if="subscriptionUpdating"
              class="i-lucide-loader-circle size-4 animate-spin"
              aria-hidden="true"
            />
            Confirmar cancelamento
          </button>
        </div>
      </template>
    </LaisModal>
  </section>
</template>
